import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "src/@/lib/utils"

// Relevo ("3D") aplicado por sombras, nunca por gradiente: gradiente é
// background-image e não conflita com `bg-*` no tailwind-merge, então venceria
// visualmente qualquer override de cor passado via className (bg-[#7C3AED],
// bg-pink-800, ...) — e o app inteiro depende desses overrides. Sombras em
// rgba neutro funcionam sobre qualquer cor de fundo.
// Anatomia (ver src/utils/buttonStyles.js para a versão comentada):
//   luz na aresta de cima + espessura na base + sombra externa curta + borda.
// Microinteração: hover sobe 1px, active desce 1px e "afunda" (sombra interna).
const RELEVO_MOTION =
  "hover:-translate-y-px active:translate-y-px transition-all duration-200 ease-out";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: [
          "bg-primary text-primary-foreground border border-black/10",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.16),inset_0_-1px_0_rgba(0,0,0,0.2),0_1px_2px_rgba(16,24,40,0.12)]",
          "hover:bg-primary/90",
          "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.18),inset_0_-1px_0_rgba(0,0,0,0.2),0_2px_5px_rgba(16,24,40,0.16)]",
          "active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.25)]",
          RELEVO_MOTION,
        ].join(" "),
        destructive: [
          "bg-destructive text-destructive-foreground border border-red-800/30",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.18),inset_0_-1px_0_rgba(80,10,10,0.28),0_1px_2px_rgba(153,27,27,0.15)]",
          "hover:bg-destructive/90",
          "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-1px_0_rgba(80,10,10,0.28),0_2px_5px_rgba(153,27,27,0.2)]",
          "active:shadow-[inset_0_1px_2px_rgba(80,10,10,0.32)]",
          RELEVO_MOTION,
        ].join(" "),
        outline: [
          "border border-input bg-background",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(16,24,40,0.04),0_1px_2px_rgba(16,24,40,0.05)]",
          "hover:bg-accent hover:text-accent-foreground",
          "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(16,24,40,0.05),0_2px_5px_rgba(16,24,40,0.08)]",
          "active:shadow-[inset_0_1px_2px_rgba(16,24,40,0.08)]",
          "dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),inset_0_-1px_0_rgba(0,0,0,0.25),0_1px_2px_rgba(0,0,0,0.25)]",
          "dark:active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)]",
          RELEVO_MOTION,
        ].join(" "),
        secondary: [
          "bg-secondary text-secondary-foreground border border-black/5",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.6),inset_0_-1px_0_rgba(16,24,40,0.05),0_1px_2px_rgba(16,24,40,0.05)]",
          "hover:bg-secondary/80",
          "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.6),inset_0_-1px_0_rgba(16,24,40,0.06),0_2px_5px_rgba(16,24,40,0.08)]",
          "active:shadow-[inset_0_1px_2px_rgba(16,24,40,0.1)]",
          "dark:border-white/5",
          RELEVO_MOTION,
        ].join(" "),
        // Flat por design: ghost vive em toolbars densas e link imita texto.
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-2xl px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    (<Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />)
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
